import { useEffect, useState, type FormEvent } from 'react';
import { isApiError } from '../../lib/api';
import {
  addIntentExamples,
  classifyIntent,
  createIntentLabel,
  deleteIntentExample,
  deleteIntentLabel,
  getIntents,
  listIntentExamples,
  patchIntentSettings,
  trainIntents,
  updateIntentLabel,
  type IntentExample,
  type IntentLabel,
  type IntentSummary,
} from '../../lib/intents-api';
import admin from './admin.module.css';
import styles from './KnowledgeBase.module.css';
import { confirmDialog } from '../../stores/confirm';

const THRESHOLD_MIN = 0.5;
const THRESHOLD_MAX = 0.99;

const pct = (p: number | null | undefined) => (p == null ? '—' : `${Math.round(p * 100)}%`);

function clampThreshold(n: number): number {
  if (!Number.isFinite(n)) return 0.75;
  return Math.max(THRESHOLD_MIN, Math.min(THRESHOLD_MAX, Math.round(n * 100) / 100));
}

const SOURCE_LABEL: Record<IntentExample['source'], string> = {
  seed: 'Default',
  admin: 'Added',
  correction: 'Agent fix',
};

// ─── Intents section ───────────────────────────────────────────────────────
// The site's local intent model (CPU-only, trained in-process): what each
// visitor message is about, used to route "wants a human" straight to an
// online agent and to break cost and outcomes down by intent. Site-admins
// manage the labels and their example phrases, retrain, and try it out.
export function IntentsSection({ siteId }: { siteId: string }) {
  const [data, setData] = useState<IntentSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [training, setTraining] = useState(false);
  const [threshold, setThreshold] = useState(0.75);
  const [open, setOpen] = useState<string | null>(null);
  const [tryText, setTryText] = useState('');
  const [predictions, setPredictions] = useState<Array<{ label: string; p: number }> | null>(null);

  async function load() {
    try {
      const d = await getIntents(siteId);
      setData(d);
      setThreshold(d.settings.handoffThreshold);
      setError(null);
    } catch {
      setError('Could not load intents.');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function saveSettings(patch: { handoffEnabled?: boolean; handoffThreshold?: number }) {
    // Optimistic, so the control reflects the click at once; load() corrects it.
    setData((d) => (d ? { ...d, settings: { ...d.settings, ...patch } } : d));
    setSaving(true);
    setError(null);
    try {
      await patchIntentSettings(siteId, patch);
      await load();
    } catch {
      setError('Could not save the handoff settings.');
    } finally {
      setSaving(false);
    }
  }

  async function retrain() {
    setTraining(true);
    setError(null);
    setNotice(null);
    try {
      const r = await trainIntents(siteId);
      setNotice(`Trained model v${r.version ?? '?'} on ${r.exampleCount} examples — held-out accuracy ${pct(r.accuracy)}.`);
      await load();
    } catch (err) {
      const code = isApiError(err) ? (err.body as { error?: string } | null)?.error : null;
      setError(code === 'need-two-labels' ? 'Add examples to at least two intents first.' : 'Training failed.');
    } finally {
      setTraining(false);
    }
  }

  async function tryIt(e: FormEvent) {
    e.preventDefault();
    if (!tryText.trim()) return;
    setError(null);
    try {
      setPredictions(await classifyIntent(siteId, tryText.trim()));
    } catch (err) {
      setPredictions(null);
      setError(isApiError(err) && err.status === 409 ? 'No model yet — retrain first.' : 'Could not classify that text.');
    }
  }

  async function toggleHandoff(label: IntentLabel) {
    try {
      await updateIntentLabel(siteId, label.id, { isHandoff: !label.isHandoff });
      await load();
    } catch {
      setError('Could not update the intent.');
    }
  }

  async function removeLabel(label: IntentLabel) {
    if (!(await confirmDialog({ title: `Delete the “${label.name}” intent?`, message: `Its ${label.exampleCount} examples are deleted too.` }))) return;
    try {
      await deleteIntentLabel(siteId, label.id);
      await load();
    } catch {
      setError('Could not delete the intent.');
    }
  }

  const nameOf = (key: string) => data?.labels.find((l) => l.key === key)?.name ?? key;

  return (
    <section className={admin.section}>
      <div className={styles.headRow}>
        <div>
          <p className={admin.kicker}>Intents</p>
          <h2 className={admin.title} style={{ marginBottom: 0 }}>
            What visitors are asking for.
          </h2>
        </div>
        <div className={styles.headActions}>
          <button type="button" className={admin.button} disabled={training} onClick={() => void retrain()}>
            {training ? 'Training…' : 'Retrain model'}
          </button>
        </div>
      </div>

      <p style={{ marginTop: 0, color: 'var(--inic-ink-subtle)', fontSize: 'var(--inic-size-sm)' }}>
        A small model trained on this site’s example phrases labels every visitor message, on this server’s CPU —
        no AI tokens spent. It tags conversations for agents and reports, and can hand chats that ask for a person
        straight to an online agent. Agents’ corrections in the inbox become new examples.
      </p>

      {data && (
        <div className={styles.ragBar}>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={data.settings.handoffEnabled}
              disabled={saving}
              onChange={(e) => void saveSettings({ handoffEnabled: e.target.checked })}
            />
            <span className={styles.toggleCopy}>
              <span className={admin.label} style={{ textTransform: 'none' }}>
                Route “wants a human” to an agent
              </span>
              <span className={admin.hint}>
                When an agent is online, a confident handoff intent skips the AI reply.
              </span>
            </span>
          </label>
          <label className={styles.topkField}>
            <span className={admin.label}>Confidence threshold</span>
            <input
              className={`${admin.input} ${styles.topkInput}`}
              type="number"
              step={0.01}
              min={THRESHOLD_MIN}
              max={THRESHOLD_MAX}
              value={threshold}
              disabled={saving || !data.settings.handoffEnabled}
              onChange={(e) => setThreshold(Number(e.target.value))}
              onBlur={(e) => {
                const v = clampThreshold(Number(e.target.value));
                setThreshold(v);
                if (v !== data.settings.handoffThreshold) void saveSettings({ handoffThreshold: v });
              }}
            />
            <span className={admin.hint}>
              Between {THRESHOLD_MIN} and {THRESHOLD_MAX}.
            </span>
          </label>
          <span className={styles.ragStatus}>{saving ? 'Saving…' : 'Saved automatically'}</span>
        </div>
      )}

      {error && <p className={admin.error}>{error}</p>}
      {notice && <p className={admin.notice}>{notice}</p>}

      {data && (
        <p className={styles.rowMeta} data-testid="intent-model-status">
          {data.model
            ? `Model v${data.model.version} · held-out accuracy ${pct(data.model.accuracy)} · ${data.model.exampleCount} examples · trained ${new Date(data.model.trainedAt).toLocaleString()}`
            : 'No model trained yet — it trains on first use, or press Retrain.'}
        </p>
      )}

      <form className={admin.form} onSubmit={tryIt} style={{ marginTop: 'var(--inic-space-4)' }}>
        <label className={admin.field} style={{ gridColumn: '1 / -1' }}>
          <span className={admin.label}>Try it</span>
          <input
            className={admin.input}
            value={tryText}
            maxLength={2000}
            onChange={(e) => setTryText(e.target.value)}
            placeholder="I was charged twice for my subscription"
          />
        </label>
        <div className={admin.formActions}>
          <button type="submit" className={admin.buttonGhost} disabled={!tryText.trim()}>
            Classify
          </button>
        </div>
        {predictions && (
          <ul className={styles.list} style={{ gridColumn: '1 / -1' }} aria-label="Predictions">
            {predictions.map((p) => (
              <li key={p.label} className={styles.row}>
                <span className={styles.rowTitleText}>{nameOf(p.label)}</span>
                <span className={styles.rowMeta}>{pct(p.p)}</span>
              </li>
            ))}
          </ul>
        )}
      </form>

      <p className={styles.subhead}>Intents</p>
      {!data ? (
        <div className={styles.empty}>Loading…</div>
      ) : (
        <div className={styles.list}>
          {data.labels.map((label) => (
            <div key={label.id}>
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>
                    {label.isHandoff && <span className={admin.badge}>Handoff</span>}
                    <span className={styles.rowTitleText}>{label.name}</span>
                  </span>
                  <span className={styles.rowMeta}>
                    {label.key} · {label.exampleCount} examples
                    {data.model?.report.perLabel?.[label.key]
                      ? ` · recall ${pct(data.model.report.perLabel[label.key]!.recall)}`
                      : ''}
                  </span>
                </div>
                <div className={admin.actions}>
                  <button
                    type="button"
                    className={admin.buttonGhost}
                    onClick={() => setOpen(open === label.id ? null : label.id)}
                  >
                    {open === label.id ? 'Hide examples' : 'Examples'}
                  </button>
                  <button type="button" className={admin.buttonGhost} onClick={() => void toggleHandoff(label)}>
                    {label.isHandoff ? 'Not handoff' : 'Mark handoff'}
                  </button>
                  <button type="button" className={admin.buttonDanger} onClick={() => void removeLabel(label)}>
                    delete
                  </button>
                </div>
              </div>
              {open === label.id && <ExamplesPanel siteId={siteId} label={label} onChanged={() => void load()} />}
            </div>
          ))}
        </div>
      )}

      <NewLabelForm siteId={siteId} onAdded={() => void load()} />
    </section>
  );
}

// ─── Examples of one intent ────────────────────────────────────────────────
function ExamplesPanel({ siteId, label, onChanged }: { siteId: string; label: IntentLabel; onChanged: () => void }) {
  const [examples, setExamples] = useState<IntentExample[] | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setExamples(await listIntentExamples(siteId, label.id));
    } catch {
      setError('Could not load examples.');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, label.id]);

  async function add(e: FormEvent) {
    e.preventDefault();
    const texts = [...new Set(draft.split('\n').map((t) => t.trim()).filter(Boolean))].slice(0, 200);
    if (texts.length === 0) return;
    setError(null);
    try {
      await addIntentExamples(siteId, label.id, texts);
      setDraft('');
      await load();
      onChanged();
    } catch {
      setError('Could not add those examples.');
    }
  }

  async function remove(example: IntentExample) {
    try {
      await deleteIntentExample(siteId, example.id);
      await load();
      onChanged();
    } catch {
      setError('Could not delete the example.');
    }
  }

  return (
    <form className={admin.form} onSubmit={add} aria-label={`Examples for ${label.name}`}>
      {error && <p className={admin.error} style={{ gridColumn: '1 / -1' }}>{error}</p>}
      <ul className={styles.list} style={{ gridColumn: '1 / -1', maxHeight: 260, overflowY: 'auto' }}>
        {(examples ?? []).map((ex) => (
          <li key={ex.id} className={styles.row}>
            <span className={styles.rowTitle}>
              <span className={admin.badge}>{SOURCE_LABEL[ex.source]}</span>
              <span className={styles.rowTitleText}>{ex.text}</span>
            </span>
            <button type="button" className={admin.buttonDanger} onClick={() => void remove(ex)}>
              delete
            </button>
          </li>
        ))}
        {examples?.length === 0 && <li className={styles.empty}>No examples yet.</li>}
      </ul>
      <label className={admin.field} style={{ gridColumn: '1 / -1' }}>
        <span className={admin.label}>Add examples — one phrase per line</span>
        <textarea className={admin.input} rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} />
        <span className={admin.hint}>Retrain afterwards to use them.</span>
      </label>
      <div className={admin.formActions}>
        <button type="submit" className={admin.buttonGhost} disabled={!draft.trim()}>
          Add examples
        </button>
      </div>
    </form>
  );
}

// ─── Add an intent ─────────────────────────────────────────────────────────
function NewLabelForm({ siteId, onAdded }: { siteId: string; onAdded: () => void }) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [isHandoff, setIsHandoff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const derivedKey = (key || name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !derivedKey) return;
    setError(null);
    try {
      await createIntentLabel(siteId, { key: derivedKey, name: name.trim(), isHandoff });
      setName('');
      setKey('');
      setIsHandoff(false);
      onAdded();
    } catch (err) {
      setError(isApiError(err) && err.status === 409 ? 'An intent with that key already exists.' : 'Could not add the intent.');
    }
  }

  return (
    <form className={admin.form} onSubmit={submit} style={{ marginTop: 'var(--inic-space-4)' }} aria-label="New intent">
      <label className={admin.field}>
        <span className={admin.label}>New intent</span>
        <input className={admin.input} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Order status" />
      </label>
      <label className={admin.field}>
        <span className={admin.label}>Key</span>
        <input
          className={admin.input}
          value={key}
          maxLength={40}
          onChange={(e) => setKey(e.target.value)}
          placeholder={derivedKey || 'order_status'}
        />
        <span className={admin.hint}>Lowercase letters, digits, underscores.</span>
      </label>
      <label className={styles.toggleField}>
        <input type="checkbox" checked={isHandoff} onChange={(e) => setIsHandoff(e.target.checked)} />
        <span className={admin.label} style={{ textTransform: 'none' }}>
          Handoff intent
        </span>
      </label>
      <div className={admin.formActions}>
        <button type="submit" className={admin.button} disabled={!name.trim() || !derivedKey}>
          Add intent
        </button>
      </div>
      {error && <p className={admin.error} style={{ gridColumn: '1 / -1' }}>{error}</p>}
    </form>
  );
}
